import clsx from 'clsx'
import {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'

import ApiExample from './ApiExample'
import {loadApiDocumentationJSON, preparePath} from './apiList'
import ApiTable from './ApiTable/ApiTable'
import ExampleCode from './ExampleCode'
import {CopyButton} from '../CopyButton'
import {Tabs} from '../Tabs'
import {BACKEND} from '~/config'
import intlMap from '~/intl/ru'
import {asr} from '~/public/static/api-documentation/api-asr'
import {speakers} from '~/public/static/api-documentation/api-speakers'
import {storage} from '~/public/static/api-documentation/api-storage'
import {tts} from '~/public/static/api-documentation/api-tts'
import {trl, interfaceLanguage} from '~/services/intl'
import {showPopup} from '~/services/popup'
import {getStaticResourceText} from '~/utils/staticResource'

import type {ApiMethods, Method, PropertyDescriptions} from './apiList'
import type {Tab} from '../Tabs'

type RouteParams = {
  chapter: 'oauth' | 'profile' | 'recognition' | 'synthesis' | 'speakers' | 'storage' | 'tts' | 'asr'
  topic?: string
}

type ResponseSectionProps = {
    code: number
    description?: string
    title: string
    contentType?: string
    json: PropertyDescriptions
    value?: string[]
}

export const initialRouteDocumentationPage = '/documentation'
export function ApiPage() {
    const {chapter = 'oauth', topic = ''} = useParams<RouteParams>()
    const [method, setMethod] = useState<Method>()
    const [staticText, setStaticText] = useState('')
    const [activeResponseTab, setActiveResponseTab] = useState<Tab<number>>()
    const navigate = useNavigate()
    const documentContent = useRef<HTMLDivElement>(null)

    useLayoutEffect(() => {
        Promise.all([
            loadApiDocumentationJSON(interfaceLanguage),
            tts[interfaceLanguage](),
            speakers[interfaceLanguage](),
            storage[interfaceLanguage](),
            asr[interfaceLanguage](),
        ])
            .then(([apiMethods, ttsMethods, speakersMethods, storageMethods, asrMethods]) => {
                //TODO временно, когда все методы рассортирую, сделаем по-человечески
                const method = ({
                    ...apiMethods,
                    tts: ttsMethods as Method[],
                    speakers: speakersMethods as Method[],
                    storage: storageMethods as Method[],
                    asr: asrMethods as Method[],
                } as ApiMethods)[chapter].find(item => preparePath(item.method.type + item.method.url) == topic)

                if (!method)
                    throw 'error'

                setMethod(method)

                Promise.resolve(
                    method.includesStatic
                        ? getStaticResourceText(`/documentation/api-${topic}.${interfaceLanguage}.html`)
                        : ''
                )
                    .then(setStaticText)
            })
            .catch(() => navigate(initialRouteDocumentationPage))
            .finally(() => documentContent.current?.scrollIntoView({behavior: 'instant'}))
    }, [chapter, topic, navigate])

    const queryTabs = [
        {value: 'payload', label: 'Payload'},
        {value: 'cUrl', label: 'cUrl'},
    ] as const

    const responseTabs = useMemo(
        () => method?.responses
            .map(({code}) => ({
                value: code,
                label: `${code}`,
                className: code < 300 ? 'api-tab_success' : 'api-tab_reject',
            })) || [],
        [method]
    )

    useEffect(() => {
        setActiveResponseTab(responseTabs[0])
    }, [responseTabs])

    if (!method)
        return null

    const url = `${BACKEND.audioUrlPrefix}${chapter == 'oauth' ? '/oauth' : '/api/v1'}${method.method.url}`
    const {description, method: methodApi, parameters, responses} = method

    function ResponseSection({code, json, title, contentType, description, value}: ResponseSectionProps) {
        return activeResponseTab?.value == code &&
            <div className='api__response'>
                <h5>{title}</h5>
                {description && <span className='text text_grey text_minimal'>{description}</span>}
                {!description && contentType && <span className='text text_grey text_minimal'>Response schema {contentType}</span>}
                {!!Object.keys(json).length && <ApiTable data={json}/>}
                {value && <>
                    <h5>{trl('Пример ответа')}</h5>
                    <ExampleCode>
                        <div dangerouslySetInnerHTML={{__html: value.join('')}}/>
                    </ExampleCode>
                </>}
            </div>
    }

    return (
        <div className='documentation__text api'>
            <div className='documentation__text-wrapper'>
                <div
                    className='documentation__text-content documentation__text-content_api'
                    ref={documentContent}
                >
                    <h1>{intlMap.documentation[chapter]}</h1>
                    <h2>{description}</h2>
                    {staticText && <div dangerouslySetInnerHTML={{__html: staticText}}/>}
                    <section className='small-tables'>
                        <h4 className='text text_nowrap'>{trl('Запрос')}</h4>
                        <div className='row row_gap-3 row_nowrap row_offset-3'>
                            <div className={clsx('api__method-type text text_uppercase', `api__method-type_${method.method.type}`)}>
                                {methodApi.type}
                            </div>
                            <span className='row row_gap-3'>
                                <button
                                    className='link'
                                    onClick={() => navigator
                                        .clipboard
                                        .writeText(url)
                                        .then(() => showPopup(trl('Скопировано')))
                                    }
                                >
                                    {url}
                                </button>

                                <CopyButton className='pi pi-clone code__button text text_grey'>
                                    {url}
                                </CopyButton>
                            </span>
                        </div>
                        {method.parameters
                            ? <>
                                {parameters?.search &&
                                    <>
                                        <h5>{trl('Search-параметры')}</h5>
                                        <ApiTable data={parameters.search}/>
                                    </>
                                }
                                {parameters?.path &&
                                    <>
                                        <h5>{trl('Path-параметры')}</h5>
                                        <ApiTable data={parameters.path}/>
                                    </>
                                }
                                {parameters?.requestBody &&
                                    <>
                                        <h5>{trl('Тело запроса')}</h5>
                                        <ApiTable data={parameters.requestBody.value}/>
                                        {!!parameters.requestBody.payload.length &&
                                            <ApiExample
                                                title={trl('Пример запроса')}
                                                tabs={queryTabs}
                                                getHtml={({value}) => parameters.requestBody?.[value].join('') || ''}
                                            />
                                        }
                                    </>
                                }
                            </>
                            : <p className='text text_grey text_minimal'>{trl('Параметры запроса отсутствуют')}</p>
                        }
                        <h4 className='text text_nowrap'>{trl('Ответ')}</h4>
                        <div className='row row_gap-5 row_nowrap row_offset-3'>
                            <Tabs
                                value={activeResponseTab?.value}
                                tabs={responseTabs}
                                onTabClick={setActiveResponseTab}
                                tabClassName='api-tab'
                                className='tabs_rounded row_offset-clear'
                            />
                        </div>
                        {responses.map(({code, response, title, contentType, description, value}, index) =>
                            <ResponseSection
                                key={index}
                                code={code}
                                title={title}
                                contentType={contentType}
                                json={response}
                                description={description}
                                value={value}
                            />
                        )}
                    </section>
                </div>
            </div>
        </div>
    )
}
